import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { PROMOCODE_MAX_LENGTH } from 'entities/promocode';
import {
	ActivatedPromocode,
	ActivatePromocodeMutationParams,
	createActivatePromocodeMutation,
	Player,
} from 'shared/api';
import {
	maxLengthRule,
	notEmptyRule,
	notify,
	createModalFormModel,
	ModalFormModel,
} from 'shared/lib';

export type ActivatePromocodeFormValue = {
	promocode: string;
};

export type ActivatePromocodeModel = ModalFormModel<Player, ActivatePromocodeFormValue> & {
	activatePromocodeMutation: Mutation<
		ActivatePromocodeMutationParams,
		ActivatedPromocode,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createActivatePromocodeModel = (): ActivatePromocodeModel => {
	const activatePromocodeMutation = createActivatePromocodeMutation();

	const model = createModalFormModel<Player, ActivatePromocodeFormValue>({
		fields: {
			promocode: {
				init: '',
				rules: [notEmptyRule, maxLengthRule(PROMOCODE_MAX_LENGTH)],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = activatePromocodeMutation;

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			player: model.$modalData,
		},
		filter: ({ player }) => player !== null && player.balance === 0,
		fn: ({ player, values }) => ({
			playerId: player!.id,
			code: values.promocode,
		}),
		target: activatePromocodeMutation.start,
	});

	sample({
		clock: model.form.submitted,
		source: model.$modalData,
		filter: (player) => player !== null && player.balance > 0,
		fn: () => 'PLAYER-BALANCE-IS-NOT-ZERO',
		target: model.form.fields.promocode.addError,
	});

	chain(activatePromocodeMutation.finished.success, model.switcher.disable);

	notify(activatePromocodeMutation.finished.success, 'activate-promocode.success');

	chain(model.modalClosed, activatePromocodeMutation.reset);

	return {
		activatePromocodeMutation,
		$pending,
		...model,
	};
};
